import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { CSS_CLASS_PREFIX } from "../constants/css";
import { cssMainSelector } from "../lib/cssSelectors";
import styled, { css } from "styled-components";
import { findPortalDataEntity } from "../lib/portalData";
import Image from "./Image";
import MediaContainer from "./MediaContainer";
import {
  allPhonesByGroup,
  onePhoneSelected,
  phoneFromFilterPhoneOrPhoneVariant,
  phoneVariantsFromPhone
} from "../lib/mainEntitySelection";
import PhoneMemoryLabel from "./PhoneMemoryLabel";
import PhoneSimCountLabel from "./PhoneSimCountLabel";
import PhoneStateLabel from "./PhoneStateLabel";
import PhoneVariantLabel from "./PhoneVariantLabel";
import PhoneVariantSelector from "./PhoneVariantSelector";
import PhoneMemorySelector from "./PhoneMemorySelector";
import PhoneChangerContainer from "../containers/PhoneChangerContainer";
import MaterialIcon from "./MaterialIcon";
import { darken } from "polished";

const Container = styled.div``;

const PhoneInfoBarWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex-wrap: wrap;
  }
`;

const PhoneChangerAndNameAndSelectorsWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex: 1 1 auto;
    justify-content: flex-start;
    order: 0;
  }
`;

const PhoneChangerWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: none;
    order: 0;
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: 
    "outer-lgtablet-down" })} {
    display: block;
  }
`;

const NameAndSelectorsWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    justify-content: space-between;
    flex: 1 1 auto;
    flex-wrap: wrap;
    order: 1;
  }
`;

const NameWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    order: 0;
    padding: 0 0 ${p => p.theme.spacing.md} 0;
  }
`;

const BrandWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
  }
`;

const BrandImageWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    width: 28px;
    vertical-align: -3px;

    img {
      max-width: 28px;
    }
  }
`;

const BrandName = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    font-size: ${p => p.theme.font.sizeXs};
    padding-left: ${p => p.theme.spacing.md};
    text-transform: uppercase;
  }
`;

const PhoneNameAndPropertiesWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex-wrap: wrap;
  }
`;

const PhoneNameWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    font-size: 25px;
    font-weight: ${p => p.theme.font.weightSemiBold};
    padding-right: ${p => p.theme.spacing.md};
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: "mdphone-down" })} {
    font-size: 15px;
  }
`;

const PhonePropertiesWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    align-self: center;
    line-height: 18px;
  }
`;

const PhoneLabelWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    padding-left: ${p => p.theme.spacing.xs};
  }
`;

const PhoneVariantAndMemorySelectorWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    order: 1;
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    display: none;
  }
`;

const OpenMobilePhoneVariantAndMemorySelectorWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    color: ${p =>
      p.theme.phoneInfoBar.openMobilePhoneVariantAndMemorySelectorColor};
    cursor: pointer;
    display: none;
    flex: 1 1 100%;
    order: 2;
    user-select: none;
    
    .${CSS_CLASS_PREFIX}-material-icon {
      color: ${p =>
        p.theme.phoneInfoBar.openMobilePhoneVariantAndMemorySelectorColor};
    }
  }

  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    display: block;
  }
`;

const mobilePhoneVariantAndMemorySelectorWrapperPadding = css `padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.md} 0 ${p =>
  p.theme.spacing.md}`;
const MobilePhoneVariantAndMemorySelectorWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p =>
      p.theme.phoneInfoBar.mobilePhoneVariantAndMemorySelectorBackgroundColor};
    display: none;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin-top: ${p => p.theme.spacing.sm};
    order: 1;
    ${mobilePhoneVariantAndMemorySelectorWrapperPadding};
    width: 100%;
  }
  
  ${p => cssMainSelector(p.theme.tdsId, { mediaSizeName: "lgphone-down" })} {
    display: flex;
  }
`;

const MobilePhoneVariantAndMemorySelectorCloseButton = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p =>
      p.theme.phoneInfoBar
        .mobilePhoneVariantAndMemorySelectorCloseButtonBackgroundColor};
    color: ${p =>
      p.theme.phoneInfoBar.mobilePhoneVariantAndMemorySelectorCloseButtonColor};
    cursor: pointer;
    flex: 1 0 100%;
    margin: ${p => p.theme.spacing.md} -${p => p.theme.spacing.md} 0 -${p =>
        p.theme.spacing.md};
    order: 2;
    padding: ${p => p.theme.spacing.sm};
    text-align: center;

    .${CSS_CLASS_PREFIX}-material-icon {
      color: ${p =>
        p.theme.phoneInfoBar
          .mobilePhoneVariantAndMemorySelectorCloseButtonColor};
    }

    &:hover,
    &:focus {
      background-color: ${p =>
        darken(
          0.05,
          p.theme.phoneInfoBar
            .mobilePhoneVariantAndMemorySelectorCloseButtonBackgroundColor
        )};
    }
  }
`;

const PhoneVariantSelectorWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    order: 0;
  }
`;

const PhoneVariantSelectorText = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    padding-bottom: ${p => p.theme.spacing.sm};
  }
`;

const PhoneVariantAndMemorySelectorSpacer = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    padding: 0 ${p => p.theme.spacing.lg};
  }
`;

const PhoneMemorySelectorWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    order: 1;
  }
`;

const PhoneMemorySelectorText = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    padding-bottom: ${p => p.theme.spacing.sm};
  }
`;

class PhoneInfoBar extends PureComponent {
  render() {
    const {
      filter,
      mobilePhoneVariantAndMemorySelectorCollapsed,
      offerType,
      onMobilePhoneVariantAndMemorySelectorClick,
      portalData,
      t,
      updateFilterPhoneId,
      updateFilterPhoneVariantId
    } = this.props;

    try {
      if (onePhoneSelected(portalData, filter)) {
        const phone = phoneFromFilterPhoneOrPhoneVariant(portalData, filter);
        const brand = findPortalDataEntity(portalData, "brand", phone.brand.id);
        const phones = allPhonesByGroup(portalData, offerType, phone);
        const phoneVariants = phoneVariantsFromPhone(
          portalData,
          offerType,
          phone
        );

        let phoneVariant;
        if (filter.phoneVariantId.length === 1) {
          phoneVariant = findPortalDataEntity(
            portalData,
            "phoneVariant",
            filter.phoneVariantId[0]
          );
        }

        return (
          <Container>
            <MediaContainer>
              <PhoneInfoBarWrapper>
                <PhoneChangerAndNameAndSelectorsWrapper>
                  <PhoneChangerWrapper>
                    <PhoneChangerContainer />
                  </PhoneChangerWrapper>
                  <NameAndSelectorsWrapper>
                    <NameWrapper>
                      <BrandWrapper>
                        <BrandImageWrapper>
                          <Image
                            alt={brand.name}
                            src={brand.image80x}
                            responsive
                          />
                        </BrandImageWrapper>
                        <BrandName>{brand.name}</BrandName>
                      </BrandWrapper>
                      <PhoneNameAndPropertiesWrapper>
                        <PhoneNameWrapper>{phone.name}</PhoneNameWrapper>
                        <PhonePropertiesWrapper>
                          {phone.memory > 0 && (
                            <PhoneLabelWrapper>
                              <PhoneMemoryLabel amount={phone.memory} />
                            </PhoneLabelWrapper>
                          )}
                          {phone.simCount > 1 && (
                            <PhoneLabelWrapper>
                              <PhoneSimCountLabel count={phone.simCount} />
                            </PhoneLabelWrapper>
                          )}
                          {phone.state.id !== 0 && (
                            <PhoneLabelWrapper>
                              <PhoneStateLabel
                                id={phone.state.id}
                                name={phone.state.name}
                              />
                            </PhoneLabelWrapper>
                          )}
                          {phoneVariant && (
                            <PhoneLabelWrapper>
                              <PhoneVariantLabel
                                name={phoneVariant.name}
                                primaryColor={phoneVariant.primaryColor}
                                secondaryColor={phoneVariant.secondaryColor}
                              />
                            </PhoneLabelWrapper>
                          )}
                        </PhonePropertiesWrapper>
                      </PhoneNameAndPropertiesWrapper>
                    </NameWrapper>
                    <PhoneVariantAndMemorySelectorWrapper>
                      <PhoneVariantSelectorWrapper>
                        <PhoneVariantSelectorText>
                          {t("colorOptions")}
                        </PhoneVariantSelectorText>
                        <PhoneVariantSelector
                          phoneVariant={phoneVariant}
                          phoneVariants={phoneVariants}
                          updateFilterPhoneVariantId={
                            updateFilterPhoneVariantId
                          }
                        />
                      </PhoneVariantSelectorWrapper>
                      <PhoneVariantAndMemorySelectorSpacer />
                      <PhoneMemorySelectorWrapper>
                        <PhoneMemorySelectorText>
                          {t("storageOptions")}
                        </PhoneMemorySelectorText>
                        <PhoneMemorySelector
                          phone={phone}
                          phones={phones}
                          updateFilterPhoneId={updateFilterPhoneId}
                        />
                      </PhoneMemorySelectorWrapper>
                    </PhoneVariantAndMemorySelectorWrapper>
                    <OpenMobilePhoneVariantAndMemorySelectorWrapper
                      onClick={onMobilePhoneVariantAndMemorySelectorClick}
                    >
                      {t("changeColorAndStorage")}{" "}
                      {mobilePhoneVariantAndMemorySelectorCollapsed !==
                        true && (
                        <MaterialIcon>arrow_drop_down</MaterialIcon>
                      )}{" "}
                      {mobilePhoneVariantAndMemorySelectorCollapsed && (
                        <MaterialIcon>arrow_drop_up</MaterialIcon>
                      )}
                    </OpenMobilePhoneVariantAndMemorySelectorWrapper>
                  </NameAndSelectorsWrapper>
                </PhoneChangerAndNameAndSelectorsWrapper>
                {mobilePhoneVariantAndMemorySelectorCollapsed === true && (
                  <MobilePhoneVariantAndMemorySelectorWrapper>
                    <PhoneVariantSelectorWrapper>
                      <PhoneVariantSelectorText>
                        {t("colorOptions")}
                      </PhoneVariantSelectorText>
                      <PhoneVariantSelector
                        phoneVariant={phoneVariant}
                        phoneVariants={phoneVariants}
                        updateFilterPhoneVariantId={updateFilterPhoneVariantId}
                      />
                    </PhoneVariantSelectorWrapper>
                    <PhoneVariantAndMemorySelectorSpacer />
                    <PhoneMemorySelectorWrapper>
                      <PhoneMemorySelectorText>
                        {t("storageOptions")}
                      </PhoneMemorySelectorText>
                      <PhoneMemorySelector
                        phone={phone}
                        phones={phones}
                        updateFilterPhoneId={updateFilterPhoneId}
                      />
                    </PhoneMemorySelectorWrapper>
                    <MobilePhoneVariantAndMemorySelectorCloseButton
                      onClick={onMobilePhoneVariantAndMemorySelectorClick}
                    >
                      <MaterialIcon>close</MaterialIcon> {t("close")}
                    </MobilePhoneVariantAndMemorySelectorCloseButton>
                  </MobilePhoneVariantAndMemorySelectorWrapper>
                )}
              </PhoneInfoBarWrapper>
            </MediaContainer>
          </Container>
        );
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }
}

PhoneInfoBar.propTypes = {
  filter: PropTypes.object.isRequired,
  mobilePhoneVariantAndMemorySelectorCollapsed: PropTypes.bool,
  offerType: PropTypes.string.isRequired,
  onMobilePhoneVariantAndMemorySelectorClick: PropTypes.func.isRequired,
  portalData: PropTypes.object.isRequired,
  updateFilterPhoneId: PropTypes.func.isRequired,
  updateFilterPhoneVariantId: PropTypes.func.isRequired
};

export default withTranslation(["phoneInfoBar"])(PhoneInfoBar);
